import { API_CALL, CALENDAR_UPDATE_PRICING_DATA, CLEAR_BOOKING_CALENDAR } from './actionConstants'

import { FETCH_CALENDARS, FETCH_CALENDARS_BY_OCCUPANCY, FETCH_TOUR_DATES } from './apiActionConstants'
import type { CalendarParams } from 'api/calendarV2'
import { getCalendars, getCalendarsByOccupancy } from 'api/calendarV2'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { tourEnquiry } from 'api/reservation'
import getObjectKey from 'lib/object/getObjectKey'
import type { AppAction } from './types/ActionTypes'
import { getChannelMarkup } from 'selectors/channelMarkupSelector'

export function clearBookingCalendar() {
  return {
    type: CLEAR_BOOKING_CALENDAR,
  }
}

interface FetchCalendarOptions {
  occupants?: Array<App.Occupants>;
  flightOrigin?: string;
  minDate?: string;
  packages?: Array<App.HotelPackage>;
  packageKeys?: Array<string>;
  regionCode?: string;
  reservationId?: string;
}

export function fetchCalendars(offerId: string, options: FetchCalendarOptions = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const offer = (state.offer.offers[offerId] ?? state.offer.offerSummaries[offerId]) as App.HotelOffer

    const packages = options.packages ?? offer?.packages ?? []
    const packagesToFetch = packages.filter(pkg =>
      // remove any keys we're already loading or have loaded
      !state.calendar.calendarsByPackageKey[pkg.uniqueKey] &&
      !state.calendar.calendarsLoading[pkg.uniqueKey],
    )
    const channelMarkup = getChannelMarkup(state)

    if (
      // offer is already loading (without knowledge of it's package keys)
      ((!options.packages && state.calendar.calendarsLoading[offerId]) ||
      // we know what package keys are loading and there are none
        (packages.length > 0 && packagesToFetch.length === 0))
    ) {
      return
    }

    dispatch({
      api: FETCH_CALENDARS,
      type: API_CALL,
      offerId: offer.id,
      packageKeys: packagesToFetch.map(pkg => pkg.uniqueKey),
      request: () => getCalendars(
        offer,
        options.regionCode || state.geo.currentRegionCode,
        {
          occupants: options.occupants ?? [],
          flightOrigin: options.flightOrigin,
          minDate: options.minDate,
          packages: packagesToFetch,
          enquiryType: state.auth.account.isSpoofed ? 'admin' : 'customer',
          reservationId: options?.reservationId,
          postPurchase: state.checkout.cart.mode,
        },
        channelMarkup,
      ),
    })
  }
}

type CalendarRequestItem = {
  occupancyKey: string
  params: CalendarParams
}

export function fetchCalendarsByOccupancy(offerId: string, options: FetchCalendarOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const offer = (state.offer.offers[offerId] ?? state.offer.offerSummaries[offerId]) as App.HotelOffer
    const channelMarkup = getChannelMarkup(state)

    const packages = options?.packages ?? []
    const requests = (options?.occupants ?? []).reduce<Array<CalendarRequestItem>>((acc, occ) => {
      const occupancyKey = generateOccupancyStringByRoom(occ)

      const pkgLoadingMap = state.calendar.calendarsByOccupancyLoading[occupancyKey] ?? {}
      const pkgLoadedMap = state.calendar.calendarsByOccupancy[occupancyKey] ?? {}

      /**
        * Fetch packages if they are both:
        * - not loading
        * - not loaded
        */
      const packagesToFetch = packages.filter(pkg => !pkgLoadingMap[pkg.uniqueKey] && !pkgLoadedMap[pkg.uniqueKey])
      if (packagesToFetch.length > 0) {
        const params: CalendarParams = {
          occupants: [occ],
          packages: packagesToFetch,
          flightOrigin: options.flightOrigin,
          minDate: options.minDate,
          enquiryType: state.auth.account.isSpoofed ? 'admin' : 'customer',
          reservationId: options.reservationId,
          postPurchase: state.checkout.cart.mode,
        }
        acc.push({ occupancyKey, params })
      }
      return acc
    }, [])

    if (requests.length === 0) {
      return
    }

    requests.forEach((request) => {
      const action: AppAction = {
        api: FETCH_CALENDARS_BY_OCCUPANCY,
        type: API_CALL,
        occupancyKey: request.occupancyKey,
        pkgKeys: request.params.packages.map(pkg => pkg.uniqueKey),
        request: () => getCalendarsByOccupancy(
          offer,
          options.regionCode || state.geo.currentRegionCode,
          request.params,
          channelMarkup,
        ),
      }
      dispatch(action)
    })
  }
}

export interface PricingData {
  hotelPrice: number
  hotelValue: number
  taxesAndFees: number
  propertyFees: number
  surcharge: number
}
export function updateCalendarPricingData(
  offerId: string,
  uniqueKey: string,
  checkIn: string,
  occupancy: string,
  data: Partial<PricingData>,
) {
  return {
    type: CALENDAR_UPDATE_PRICING_DATA,
    offerId,
    uniqueKey,
    checkIn,
    occupancy,
    data,
  }
}

export function getTourDates(
  tourId: string,
  tourOptionId: string,
  duration: number,
  adults: number,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const key = getObjectKey({ tourId, tourOptionId, duration, adults })

    if (
      state.calendar.calendarsLoading[key] ||
      state.calendar.calendarErrors[key] ||
      state.calendar.tourDates[key]
    ) {
      // already done, no need to try again
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DATES,
      request: () => tourEnquiry(tourId, tourOptionId, duration, adults),
      key,
    })
  }
}
